import {
  BitkitAlert,
  BitkitButton,
  BitkitDialog,
  BitkitLink,
  BitkitTextArea,
  BitkitTooltip,
  IconOpenInNew,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Text } from '@chakra-ui/react/text';
import { useCallback, useContext, useState } from 'react';

import useRequestSupport from '@/components/BuildCache/hooks/useRequestSupport';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';

interface RequestSupportDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const learnMoreUrl =
  'https://docs.bitrise.io/en/bitrise-build-cache/getting-started-with-the-build-cache/getting-started-with-the-build-cache.html';

const RequestSupportDialog = ({ isOpen, onClose }: RequestSupportDialogProps) => {
  const { account } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;

  const [isRequestSubmitted, setIsRequestSubmitted] = useState(false);

  const [message, setMessage] = useState('');

  const onRequestSubmitProcessed = useCallback(() => {
    setIsRequestSubmitted(true);
  }, []);

  const {
    submit,
    isPending: isSubmitting,
    isError: isSubmitRequestError,
  } = useRequestSupport(account.slug, message, onRequestSubmitProcessed);

  return (
    <BitkitDialog
      open={isOpen}
      onOpenChange={({ open }) => {
        if (!open) onClose();
      }}
      title={isRequestSubmitted ? 'Request submitted' : 'Request support'}
      footerButtons={
        !isRequestSubmitted ? (
          <BitkitTooltip disabled={message.trim().length > 0} text="Fill out a message to contact you back">
            <BitkitButton
              onClick={submit}
              state={isSubmitting ? 'loading' : message.trim().length === 0 ? 'disabled' : undefined}
            >
              Submit
            </BitkitButton>
          </BitkitTooltip>
        ) : undefined
      }
      footerContent={
        <BitkitLink href={learnMoreUrl} isExternal colorVariant="purple" suffixIcon={IconOpenInNew}>
          Remote cache documentation
        </BitkitLink>
      }
    >
      {/* BitkitDialog doesn't spread rest props onto Dialog.Content, so data-* attributes have to live on the body. */}
      {!isRequestSubmitted && (
        <Box display="flex" flexDirection="column" gap="16" data-clarity-unmask="true">
          <Text>Leave your message and dedicated trial team will reach out to you as soon as possible. </Text>
          <BitkitTextArea
            label="How can we help?"
            placeholder="Support message"
            state={isSubmitting ? 'disabled' : undefined}
            onChange={(e) => setMessage(e.target.value)}
          />
          {isSubmitRequestError && <BitkitAlert variant="critical" messageText="Failed to send support request." />}
        </Box>
      )}
      {isRequestSubmitted && (
        <Box display="flex" flexDirection="column" gap="16" data-clarity-unmask="true">
          <Box display="flex" gap="12">
            <Text textStyle="body/lg/regular">
              We have received your message and will get back to you as soon as possible.
            </Text>
          </Box>
        </Box>
      )}
    </BitkitDialog>
  );
};

export default RequestSupportDialog;
